/**
 * How a scope's rules are authored: an AND-list of (dimension, values).
 *
 * WHAT THE GRAMMAR IS, EXACTLY
 * ----------------------------
 * `build_predicate` joins the fragments with `" AND "` and each fragment
 * is `col IN (?, …)`. There is no OR and no nesting. So the editor
 * offers an AND-list, a key may appear at most once (the predicate
 * refuses `duplicate_dimension_rule`), and neither the key nor the
 * values are ever free text.
 *
 * A union that this grammar cannot express — two groups ruling on
 * DIFFERENT dimension sets — is refused by the backend with a 409 naming
 * both, because merging the value lists would produce the CROSS PRODUCT
 * and hand each side the other's rows. That refusal belongs to the
 * effective-scope recompute; this editor's part is simply never to
 * author the merge.
 *
 * WHY A REFERENCE PROJECT
 * -----------------------
 * A scope is per TENANT and names KEYS; `dimensions` is per PROJECT and
 * owns the VALUES (`uq_dimension_project_key`). So before the picker can
 * offer anything it needs to know whose live file to read. The catalogue
 * (`GET /api/builder/admin/scopes/dimension-keys`) is the bridge: per
 * key, the projects that declare it USABLE FOR SCOPING and the
 * `dimension_id` J2's value endpoint is keyed by.
 *
 * The default reference is the first project the catalogue lists for
 * that key, and it is a UI convenience rather than a data rule — which
 * is why the project is a visible `Select` and not a hidden choice. A
 * key present in two projects with different value sets would otherwise
 * let an analyst author against the narrower one without noticing.
 *
 * WHAT IT DOES NOT DO
 * -------------------
 * It does not validate the rules a second time. `validate_rules_shape`
 * is the ONE parser and its docstring says why: *"two parsers of the
 * same JSON is how `op` ends up rejected at save and ignored at build
 * (or the reverse, which is worse)."* The editor keeps the SHAPE
 * unauthorable (no free key, no free value, no duplicate key) and lets
 * the server be the judge of the content.
 */

import { Button, Select, type SelectOption } from "@omnitok/ui";
import { Callout, EmptyState, FormField } from "@pervasivemind/admin-ui";
import { Layers, Trash2 } from "lucide-react";
import { useMemo } from "react";

import { useT } from "@/i18n";
import type { DimensionKeyOut, ScopeRule } from "@/lib/access-api";
import { DimensionValuePicker } from "@/pages/admin-access/DimensionValuePicker";

/** Which project's live file each row reads its values from. Kept
 *  beside the rules rather than inside them: it is an authoring aid and
 *  is NOT part of `rules_json`, which is tenant-wide. */
export type ReferenceProjects = Record<string, string>;

/** The keys still offerable for a NEW row: the catalogue minus the ones
 *  already used. Exported so a test can assert it without a DOM — the
 *  predicate refuses `duplicate_dimension_rule`, so offering a used key
 *  would author a scope that can only ever fail. */
export function offerableKeys(
  catalogue: readonly DimensionKeyOut[],
  rules: readonly ScopeRule[],
): DimensionKeyOut[] {
  const used = new Set(rules.map((r) => r.dimension_key));
  return catalogue.filter((entry) => !used.has(entry.key));
}

/** The project whose values a row reads: the explicit choice, else the
 *  first project the catalogue lists for that key. `null` when the key
 *  is not in the catalogue at all — an existing rule on a dimension
 *  that has since lost `usable_as: scope`, which is a real state and
 *  gets a warning rather than an empty picker. */
export function referenceFor(
  catalogue: readonly DimensionKeyOut[],
  refs: ReferenceProjects,
  key: string,
): { project_slug: string; dimension_id: string } | null {
  const entry = catalogue.find((c) => c.key === key);
  if (entry === undefined || entry.projects.length === 0) return null;
  const chosen = refs[key];
  const hit =
    entry.projects.find((p) => p.project_slug === chosen) ?? entry.projects[0];
  return { project_slug: hit.project_slug, dimension_id: hit.dimension_id };
}

export function ScopeRulesEditor({
  catalogue,
  rules,
  references,
  onChange,
  onReferenceChange,
}: {
  catalogue: readonly DimensionKeyOut[];
  rules: readonly ScopeRule[];
  references: ReferenceProjects;
  onChange: (next: ScopeRule[]) => void;
  onReferenceChange: (next: ReferenceProjects) => void;
}): JSX.Element {
  const { t } = useT();
  const offerable = useMemo(
    () => offerableKeys(catalogue, rules),
    [catalogue, rules],
  );

  const addRow = (key: string): void => {
    if (key === "") return;
    onChange([...rules, { dimension_key: key, values: [] }]);
  };

  const removeRow = (index: number): void => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const setValues = (index: number, values: unknown[]): void => {
    onChange(rules.map((r, i) => (i === index ? { ...r, values } : r)));
  };

  if (catalogue.length === 0) {
    return (
      <EmptyState
        icon={<Layers size={24} />}
        title={t("access.scopes.rules.noKeysTitle")}
        description={t("access.scopes.rules.noKeysDesc")}
      />
    );
  }

  return (
    <div className="space-y-4">
      {rules.length === 0 ? (
        <Callout tone="warning" title={t("access.scopes.rules.emptyTitle")}>
          {t("access.scopes.rules.emptyDesc")}
        </Callout>
      ) : (
        <p className="text-[13px] text-neutral-600">
          {t("access.scopes.rules.andHint")}
        </p>
      )}

      {rules.map((rule, index) => {
        const entry = catalogue.find((c) => c.key === rule.dimension_key);
        const ref = referenceFor(catalogue, references, rule.dimension_key);
        return (
          <div
            key={rule.dimension_key}
            className="rounded-lg border border-neutral-200 bg-white p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium text-neutral-900">
                  {rule.dimension_key}
                </p>
                <p className="text-[13px] text-neutral-500">
                  {t("access.scopes.rules.declaredIn", {
                    n: entry?.projects.length ?? 0,
                  })}
                </p>
              </div>
              <Button
                variant="danger"
                ghost
                size="xs"
                leftIcon={<Trash2 size={12} />}
                onClick={() => removeRow(index)}
              >
                {t("access.scopes.rules.removeRule")}
              </Button>
            </div>

            {ref === null ? (
              <Callout
                tone="warning"
                title={t("access.scopes.rules.orphanTitle")}
                className="mt-3"
              >
                {t("access.scopes.rules.orphanDesc", {
                  key: rule.dimension_key,
                })}
              </Callout>
            ) : (
              <div className="mt-3 space-y-3">
                <FormField
                  label={t("access.scopes.rules.reference")}
                  help={t("access.scopes.rules.referenceHelp")}
                >
                  <Select
                    aria-label={t("access.scopes.rules.reference")}
                    value={ref.project_slug}
                    options={(entry?.projects ?? []).map(
                      (p): SelectOption => ({
                        value: p.project_slug,
                        label: p.project_name,
                      }),
                    )}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      onReferenceChange({
                        ...references,
                        [rule.dimension_key]: e.target.value,
                      })
                    }
                  />
                </FormField>

                <DimensionValuePicker
                  projectSlug={ref.project_slug}
                  dimensionId={ref.dimension_id}
                  selected={rule.values}
                  onChange={(values) => setValues(index, values)}
                />
              </div>
            )}
          </div>
        );
      })}

      {offerable.length === 0 ? (
        <p className="text-[13px] text-neutral-500">
          {t("access.scopes.rules.allKeysUsed")}
        </p>
      ) : (
        <div className="max-w-md">
          <FormField
            label={t("access.scopes.rules.addLabel")}
            help={t("access.scopes.rules.addHelp")}
          >
            <Select
              aria-label={t("access.scopes.rules.addLabel")}
              value=""
              options={[
                { value: "", label: t("access.scopes.rules.addPlaceholder") },
                ...offerable.map(
                  (entry): SelectOption => ({
                    value: entry.key,
                    label: t("access.scopes.rules.keyOption", {
                      key: entry.key,
                      n: entry.projects.length,
                    }),
                  }),
                ),
              ]}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                addRow(e.target.value)
              }
            />
          </FormField>
        </div>
      )}
    </div>
  );
}
